import {
  CheckmarkInput,
  ChevronSmall,
  Disclaimer,
  Div,
  H5,
  Icon,
  lightTheme,
  P,
  VDriveStrategySection,
} from '@vgrubs/components';
import { useState } from 'react';
import {
  confidenceLabel,
  sectionEstimates,
  sectionValueLines,
} from '../helpers/strategySuggestionFormat';
import {
  Clamped,
  EstimateBadge,
  MutedToggle,
  ReadMoreToggle,
  SectionCard,
  SectionHead,
  ValueColumn,
  ValueCompare,
} from '../StrategySuggestion.styled';

/** Past roughly three lines the reasoning folds behind "Show more". */
const LONG_REASONING = 220;

type Props = {
  section: VDriveStrategySection;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

const Reasoning = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false);
  const long = text.length > LONG_REASONING;
  return (
    <Div $flex $column $gap="4px">
      <Clamped $open={open || !long}>
        <P $m="0" $color={lightTheme.darkGray}>
          {text}
        </P>
      </Clamped>
      {long && (
        <ReadMoreToggle type="button" onClick={() => setOpen((prev) => !prev)}>
          <Disclaimer $m="0" $color={lightTheme.primary} $underline>
            {open ? 'Show less' : 'Show more'}
          </Disclaimer>
        </ReadMoreToggle>
      )}
    </Div>
  );
};

const ValueLines = ({ lines }: { lines: string[] }) => (
  <>
    {lines.map((line, i) => (
      <P key={i} $m="0">
        {line}
      </P>
    ))}
  </>
);

/** A section the AI leaves alone: grey, folded, its reasoning one click away. */
const UnchangedSection = ({ section }: { section: VDriveStrategySection }) => {
  const [open, setOpen] = useState(false);
  return (
    <SectionCard $muted>
      <MutedToggle
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Div $flex $alignCenter $gap="8px" $wrap>
          <P $m="0" $bold $color={lightTheme.darkGray}>
            {section.title}
          </P>
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            No change
          </Disclaimer>
        </Div>
        <Icon $rotate={open ? '180deg' : '0'}>
          <ChevronSmall />
        </Icon>
      </MutedToggle>
      {open && section.reasoning && <Reasoning text={section.reasoning} />}
    </SectionCard>
  );
};

/**
 * One part of the strategy screen as the AI would change it: ticked by
 * default when the change passed validation, shown but locked (with the
 * backend's reasons in red) when it did not.
 */
export const SuggestionSection = ({
  section,
  checked,
  onCheckedChange,
}: Props) => {
  if (!section.changed) return <UnchangedSection section={section} />;

  const { applicable } = section;
  const estimates = sectionEstimates(section);

  return (
    <SectionCard>
      <SectionHead $clickable={applicable}>
        <CheckmarkInput
          checked={applicable && checked}
          disabled={!applicable}
          onChange={onCheckedChange}
          ariaLabel={`Apply ${section.title}`}
        />
        <Div $flex $column $gap="2px" $width="100%">
          <Div $flex $alignCenter $between $gap="8px" $wrap>
            <H5 $m="0">{section.title}</H5>
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              {confidenceLabel(section.confidence)}
            </Disclaimer>
          </Div>
          {section.expectedImpact && (
            <P $m="0" $color={lightTheme.darkGray}>
              {section.expectedImpact}
            </P>
          )}
        </Div>
      </SectionHead>

      {estimates.length > 0 && (
        <Div $flex $gap="6px" $wrap>
          {estimates.map((estimate) => (
            <EstimateBadge key={estimate.label} $tone={estimate.tone}>
              {estimate.label}
            </EstimateBadge>
          ))}
        </Div>
      )}

      <ValueCompare>
        <ValueColumn>
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            Now
          </Disclaimer>
          <ValueLines lines={sectionValueLines(section.key, section.current)} />
        </ValueColumn>
        <ValueColumn $suggested>
          <Disclaimer $m="0" $color={lightTheme.green}>
            Suggested
          </Disclaimer>
          <ValueLines
            lines={sectionValueLines(section.key, section.suggested)}
          />
        </ValueColumn>
      </ValueCompare>

      {section.reasoning && <Reasoning text={section.reasoning} />}

      {!applicable && section.errors.length > 0 && (
        <Div $flex $column $gap="4px">
          {section.errors.map((error) => (
            <P key={error} $m="0" $color={lightTheme.primary}>
              {error}
            </P>
          ))}
        </Div>
      )}
    </SectionCard>
  );
};
